"use client";

import { Text } from "@stone-payments/jade";
import Link, { type LinkProps } from "next/link";
import { Skeleton } from "@/components/skeleton";
import { Image } from "@/components/image";
import { cn } from "@/utils/classnames";
import { type Product } from "@/actions/schemas/products";
import { type Store } from "@/actions/schemas/stores";
import { ProductPrice } from "@/components/product-price";
import { ProductCardAddButton } from "@/components/product-card/product-card-add-button";

type ProductCardBase = Pick<LinkProps, "onClick"> & {
  store: Store;
  data: Product;
  hasBorder?: boolean;
  className?: string;
  layout?: "horizontal" | "grid";
};

type ProductCardCartButton =
  | { showCartButton: boolean; addToCart: (product: Product) => void }
  | { showCartButton?: false; addToCart?: (product: Product) => void };

type ProductCardLoadingProps = Pick<ProductCardBase, "hasBorder" | "layout">;

type ProductCardProps =
  | ({ loading: true } & Partial<ProductCardBase> & {
      showCartButton?: boolean;
      addToCart?: (product: Product) => void;
    })
  | ({ loading?: false } & ProductCardBase & ProductCardCartButton);

function ProductCardLoading({ hasBorder, layout }: ProductCardLoadingProps) {
  return (
    <div
      className={cn(
        "flex border border-(--theme-color-border-low) md:rounded-jade-xlarge",
        { "border-0 pb-4 md:pb-0": hasBorder },
        {
          "flex-row gap-x-3 md:p-2": layout === "horizontal",
          "flex-col gap-y-2 md:p-2 md:pb-3": layout === "grid",
        }
      )}
    >
      <div
        className={cn({
          "order-2 md:order-1": layout === "horizontal",
        })}
      >
        <div
          className={cn({
            "!w-28": layout === "horizontal",
          })}
        >
          <Image alt="Carregando imagem" loading={true} />
        </div>
      </div>

      <div
        className={cn("flex flex-col gap-y-1", {
          "order-1 flex-1 justify-center md:order-2": layout === "horizontal",
        })}
      >
        <div>
          <Skeleton className="!h-5 !w-30" rounded={true} />
        </div>

        <div>
          <Skeleton
            className={cn({
              "!h-12": layout === "horizontal",
              "!h-8": layout === "grid",
            })}
            rounded={true}
          />
        </div>

        <div>
          <Skeleton className="!h-5 !w-15" rounded={true} />
        </div>
      </div>
    </div>
  );
}

function ProductCard({
  store,
  data,
  loading,
  onClick,
  addToCart,
  className = "",
  hasBorder = true,
  layout = "horizontal",
  showCartButton = false,
}: ProductCardProps) {
  const hasVariants = (data?.variants?.length || 1) > 1;

  if (loading) {
    return <ProductCardLoading hasBorder={hasBorder} layout={layout} />;
  }

  return (
    <div
      className={cn(className, {
        "size-full border-b border-(--theme-color-border-low) pb-4 md:rounded-jade-xlarge md:border-0 md:pb-0":
          hasBorder && layout === "horizontal",
      })}
    >
      <Link
        className={cn(
          className,
          "group/product-card relative flex rounded-jade-xlarge bg-transparent transition-colors duration-100 outline-none",
          "before:absolute before:-m-2 before:block before:h-[calc(100%+1rem)] before:w-[calc(100%+1rem)] before:rounded-jade-xlarge before:transition-colors before:duration-100 hover:before:bg-(--theme-color-background-neutral-subtle) md:before:hidden",
          "md:hover:bg-(--theme-color-background-neutral-subtle)",
          "border-(--theme-color-border-low) before:border-2 before:border-transparent focus-visible:before:border-black md:rounded-jade-xlarge md:border md:focus-visible:border-black",
          {
            "size-full flex-row gap-x-3 md:p-2": layout === "horizontal",
            "size-full flex-col gap-y-2 md:p-2 md:pb-3": layout === "grid",
          }
        )}
        href={`/${store.identifier || store.id}/produto/${data.id}`}
        onClick={onClick}
        itemScope
        itemType="https://schema.org/Product"
        data-testid="product-card-link"
      >
        <div
          className={cn("relative", {
            "order-2 md:order-1": layout === "horizontal",
          })}
        >
          {/*
          // Use this when Product got tag implemented

          {data.tag && (
            <div
              className={cn("absolute top-0 p-1.5", {
                "right-0 md:left-0": layout === "horizontal",
                "left-0": layout === "grid",
              })}
            >
              <Tag size="small" solid={true} variant="neutral">
                {data.tag}
              </Tag>
            </div>
          )}
        */}

          <figure className={cn({ "w-29": layout === "horizontal" })}>
            <Image
              src={data.images[0]?.url ?? ""}
              alt={`Imagem do produto ${data.name} da loja ${store.name}`}
              itemProp="image"
            />
          </figure>

          {showCartButton && addToCart && !hasVariants && (
            <div className="absolute right-1 bottom-0 p-1.5 md:right-auto md:left-0">
              <ProductCardAddButton product={data} onAdd={addToCart} />
            </div>
          )}
        </div>

        <div
          className={cn("relative flex flex-col gap-y-1", {
            "order-1 flex-1 justify-between md:order-2 md:justify-start":
              layout === "horizontal",
          })}
        >
          <div>
            <div>
              <Text
                className="!line-clamp-2 !break-words !hyphens-auto"
                color="high"
                variant="text-medium"
                weight="semibold"
                itemProp="name"
              >
                {data.name}
              </Text>
            </div>

            {data.description && (
              <div>
                <Text
                  className={cn("!break-words break-all !hyphens-auto", {
                    "!line-clamp-3": layout === "horizontal",
                    "!line-clamp-2": layout === "grid",
                  })}
                  color="medium"
                  variant="text-small"
                  weight="medium"
                  itemProp="description"
                >
                  {data.description}
                </Text>
              </div>
            )}

            <ProductPrice variants={data.variants} />
          </div>
        </div>
      </Link>
    </div>
  );
}

export { ProductCard, type ProductCardProps };
